import { useEffect, useState } from "react";
import { ChevronDown, Trash2, Send, Loader2, MailOpen } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { api, formatApiError } from "@/lib/api";

const STATUS_STYLES = {
  new: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  responded: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  booked: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  archived: "bg-white/5 text-[#9CA3AF] border-white/10",
};

const selectCls = "bg-[#0D0E11] border border-white/15 rounded-lg px-2.5 py-1.5 text-xs text-[#F3F4F6] outline-none focus:border-amber-500/60";

export default function InquiriesInbox({ onChanged }) {
  const [inquiries, setInquiries] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const load = () => api.get("/inquiries").then((r) => setInquiries(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addNote = async (id) => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await api.patch(`/inquiries/${id}`, { note: noteText.trim() });
      setNoteText("");
      toast.success("Note added");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingNote(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/inquiries/${id}`);
      toast.success("Inquiry deleted");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (inquiries === null) return <div className="py-16 text-center font-mono-tactical text-xs tracking-[0.3em] text-amber-500 animate-pulse-amber">LOADING INQUIRIES…</div>;

  if (inquiries.length === 0)
    return (
      <div className="py-16 text-center text-[#6B7280]" data-testid="inquiries-empty">
        <MailOpen size={28} className="mx-auto mb-4 text-[#3a3f4d]" />
        No inquiries yet. Share your landing page — submissions land here instantly.
      </div>
    );

  return (
    <div className="space-y-3" data-testid="inquiries-list">
      {inquiries.map((inq) => (
        <div key={inq.id} className="bg-[#151821] border border-white/10 rounded-xl overflow-hidden" data-testid={`inquiry-row-${inq.id}`}>
          <button onClick={() => setOpenId(openId === inq.id ? null : inq.id)} className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-white/[0.02] transition-colors" data-testid={`inquiry-toggle-${inq.id}`}>
            <span className={`px-2.5 py-1 rounded-full border text-[10px] font-mono-tactical tracking-wider uppercase ${STATUS_STYLES[inq.status] || STATUS_STYLES.new}`} data-testid={`inquiry-status-${inq.id}`}>
              {inq.status}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#F3F4F6] truncate">{inq.facility_name}</div>
              <div className="text-xs text-[#6B7280] truncate">
                {inq.contact_name}
                {inq.contact_role ? ` — ${inq.contact_role}` : ""} · {inq.facility_type} {inq.state ? `· ${inq.state}` : ""}
              </div>
            </div>
            <span className="hidden sm:inline font-mono-tactical text-[10px] text-[#6B7280]">{new Date(inq.created_at).toLocaleDateString()}</span>
            <ChevronDown size={16} className={`text-[#6B7280] transition-transform ${openId === inq.id ? "rotate-180" : ""}`} />
          </button>

          {openId === inq.id && (
            <div className="px-5 pb-5 pt-1 border-t border-white/10" data-testid={`inquiry-detail-${inq.id}`}>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="font-mono-tactical text-[10px] tracking-[0.2em] text-[#6B7280] mb-1">EMAIL</div>
                  <a href={`mailto:${inq.email}`} className="text-amber-500 hover:underline" data-testid={`inquiry-email-${inq.id}`}>{inq.email}</a>
                </div>
                {inq.phone && (
                  <div>
                    <div className="font-mono-tactical text-[10px] tracking-[0.2em] text-[#6B7280] mb-1">PHONE</div>
                    <span className="text-[#9CA3AF]">{inq.phone}</span>
                  </div>
                )}
              </div>
              {inq.message && (
                <div className="mt-4">
                  <div className="font-mono-tactical text-[10px] tracking-[0.2em] text-[#6B7280] mb-1">MESSAGE</div>
                  <p className="text-sm text-[#9CA3AF] leading-relaxed bg-[#0D0E11] border border-white/10 rounded-lg p-4">{inq.message}</p>
                </div>
              )}

              <div className="mt-4">
                <div className="font-mono-tactical text-[10px] tracking-[0.2em] text-[#6B7280] mb-2">NOTES</div>
                {(inq.notes || []).map((n, i) => (
                  <div key={i} className="text-xs text-[#9CA3AF] border-l-2 border-amber-500/40 pl-3 py-1 mb-1">
                    {n.text} <span className="text-[#6B7280]">· {new Date(n.at).toLocaleDateString()}</span>
                  </div>
                ))}
                <div className="flex gap-2 mt-2">
                  <input
                    value={openId === inq.id ? noteText : ""}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add a note (call outcome, next step…)"
                    className="flex-1 bg-[#0D0E11] border border-white/10 focus:border-amber-500/60 rounded-lg px-3 py-2 text-xs text-[#F3F4F6] outline-none"
                    data-testid={`inquiry-note-input-${inq.id}`}
                  />
                  <button onClick={() => addNote(inq.id)} disabled={savingNote} className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0D0E11] text-xs font-bold px-4 py-2 rounded-lg" data-testid={`inquiry-note-add-${inq.id}`}>
                    {savingNote ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Add
                  </button>
                </div>
              </div>

              <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="font-mono-tactical text-[10px] tracking-[0.2em] text-[#6B7280]">STATUS</span>
                  <select value={inq.status} onChange={(e) => setStatus(inq.id, e.target.value)} className={selectCls} data-testid={`inquiry-status-select-${inq.id}`}>
                    {Object.keys(STATUS_STYLES).map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <button onClick={() => remove(inq.id)} className="inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-red-400 transition-colors" data-testid={`inquiry-delete-${inq.id}`}>
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
